import { getQuoteOutcome } from '../services/quote-outcome';
import { projectHasCompletedQuote, projectHasStage5Presentation } from './project-stage';

export interface BottomNavQuotesFilterResult {
    value: string;
    counts: {
        all: number;
        won: number;
        pending: number;
        lost: number;
    };
}

export interface BottomNavDashboardPulseResult {
    activeToday: number;
    todayValue: number;
    unsentCount: number;
    unsentValue: number;
    oldestUnsentDays: number;
}

export function computeBottomNavQuotesFilter(
    projects: any[] | null | undefined,
    quotesFilter: string
): BottomNavQuotesFilterResult {
    const list = Array.isArray(projects) ? projects : [];
    // Canonical outcome (quote-outcome.js) collapsed to the
    // nav's won / pending / lost buckets. Fixes a Stage-5 win
    // (quote_status default 'pending') being counted as Pipeline.
    const statusOf = (p: any) => {
        const o = getQuoteOutcome(p);
        return (o === 'won' || o === 'lost') ? o : 'pending';
    };
    const counts: Record<string, number> = { all: list.length, won: 0, pending: 0, lost: 0 };
    for (const p of list) {
        const s = statusOf(p);
        if (counts[s] != null) counts[s] += 1;
    }
    return { value: quotesFilter, counts: counts as BottomNavQuotesFilterResult['counts'] };
}

export function computeBottomNavDashboardPulse(
    projects: any[] | null | undefined,
    now: number = Date.now()
): BottomNavDashboardPulseResult {
    const DAY = 86400000;
    const list = Array.isArray(projects) ? projects : [];

    const getQuoteValue = (q: any) => {
        const scope = q.quoteData?.scope || q.scope || [];
        const raw = scope.reduce((s: number, i: any) => {
            const n = parseFloat((i.amount || '0').toString().replace(/,/g, ''));
            return s + (Number.isFinite(n) ? n : 0);
        }, 0);
        const isIncGst = q.quoteData?.isGstInclusive ?? q.scopeGstInclusive ?? true;
        return isIncGst ? Math.round(raw / 1.1 * 100) / 100 : raw;
    };

    const touchedToday = list.filter((q: any) => {
        const t = q.updated_at || q.updatedAt || q.lastModified || q.created_at || q.createdAt;
        return t && (now - new Date(t).getTime()) <= DAY;
    });
    const activeToday = touchedToday.length;
    const todayValue = Math.round(touchedToday.reduce((s: number, q: any) => s + getQuoteValue(q), 0));

    // The bar carries the UNSENT backlog instead of "touched today".
    const unsent = list.filter((q: any) => {
        if ((q.projectType || q.project_type) === 'history') return false;
        const outcome = getQuoteOutcome(q);
        if (outcome === 'won' || outcome === 'lost' || outcome === 'expired' || outcome === 'sent') return false;
        return projectHasCompletedQuote(q) || projectHasStage5Presentation(q);
    });
    const unsentValue = Math.round(unsent.reduce((s: number, q: any) => s + getQuoteValue(q), 0));
    const oldestUnsentDays = unsent.reduce((worst: number, q: any) => {
        const t = q.createdAt || q.created_at;
        const ms = t ? new Date(t).getTime() : NaN;
        if (!Number.isFinite(ms)) return worst;
        return Math.max(worst, Math.floor((now - ms) / DAY));
    }, 0);

    return {
        activeToday,
        todayValue,
        unsentCount: unsent.length,
        unsentValue,
        oldestUnsentDays,
    };
}
